import { Field, FieldLabel } from "@nt/ui/field";
import { CircleAlert } from "lucide-react";
import { useState } from "react";

import {
  REQUEST_RESET_PATH,
  requestPasswordReset,
} from "../auth/password-reset";
import { AuthLayout } from "./auth-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@nt/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Signed-out screen: ask for the email a reset link goes to. Rendered outside
 * the gate (`console-routes.tsx`), since the operator by definition cannot
 * sign in yet.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const outcome = await requestPasswordReset({ email });
    setPending(false);
    if (outcome.ok) setSent(true);
    else setError(outcome.message);
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We’ll email you a link to set a new one."
    >
      <div className="space-y-6">
        {error ? (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}

        {sent ? (
          // Worded for both cases on purpose: the service answers the same
          // whether or not the address has an account.
          <Alert role="status">
            <AlertDescription>
              If an account exists for that email, a reset link is on its way.
              Check your inbox.
            </AlertDescription>
          </Alert>
        ) : (
          <form
            action={REQUEST_RESET_PATH}
            method="post"
            onSubmit={(event) => void submit(event)}
            className="space-y-4"
          >
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@nonresident.tax"
                className="h-11"
              />
            </Field>
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={pending}
            >
              {pending ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Remembered it?{" "}
          <a
            href="/"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Back to sign in
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
